import Misc from '@components/Misc';
import QuickCreateRelatedModel from '@components/Inputs/QuickCreateRelatedModel';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Grid, TextInput, Typography } from '@ts/storybook';
import { useTranslation } from 'react-i18next';
import { IForm } from '@ts/ui-models';

interface IWipPartInformationProps extends IForm {}

const WipPartInformation = (props: IWipPartInformationProps) => {
  const { onCallbackChange, values, mode } = props;
  const { t } = useTranslation();
  const isViewMode = mode === 'view';

  //#region Renders
  const _renderManufacturer = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{t('Manufacturer')}</Misc.PaperSectionTitle>
        <br />
        <TextInput
          isReadOnly={isViewMode}
          label="Manufacturer"
          value={values.manufacturer}
          onChangeValue={onCallbackChange('manufacturer')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Manufacturer part number"
          value={values.manufacturerPartNumber}
          onChangeValue={onCallbackChange('manufacturerPartNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Manufacturer part number revision"
          value={values.manufacturerPartNumberRevision}
          onChangeValue={onCallbackChange('manufacturerPartNumberRevision')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Manufacturer serial number"
          value={values.manufacturerSerialNumber}
          onChangeValue={onCallbackChange('manufacturerSerialNumber')}
        />
      </Misc.NewPaperWithPadding>
    );
  };

  const _renderSupplier = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{t('Supplier')}</Misc.PaperSectionTitle>
        <br />
        <TextInput
          isReadOnly={isViewMode}
          label="Supplier"
          value={values.supplier}
          onChangeValue={onCallbackChange('supplier')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Supplier part number"
          value={values.supplierPartNumber}
          onChangeValue={onCallbackChange('supplierPartNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Supplier serial number"
          value={values.supplierSerialNumber}
          onChangeValue={onCallbackChange('supplierSerialNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Purchase order to supplier"
          value={values.supplierPurchaseOrder}
          onChangeValue={onCallbackChange('supplierPurchaseOrder')}
        />
      </Misc.NewPaperWithPadding>
    );
  };

  const _renderProduction = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12 }}>
            <Typography variant="h6" text="Production" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              value={values.productionJobId}
              onChange={onCallbackChange('productionJobId')}
              isReadOnly={isViewMode}
              searchComponent={generateSearchEntitySelector('productionJob')}
              label="Production Job"
              checked={values.createProductionJob || false}
              onCheck={onCallbackChange('createProductionJob')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              isReadOnly={isViewMode}
              value={values.productionBatchId}
              onChange={onCallbackChange('productionBatchId')}
              searchComponent={generateSearchEntitySelector('productionBatch')}
              label="Production Batch"
              checked={values.createProductionBatch || false}
              onCheck={onCallbackChange('createProductionBatch')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              isReadOnly={isViewMode}
              value={values.productionSeriesId}
              onChange={onCallbackChange('productionSeriesId')}
              searchComponent={generateSearchEntitySelector('productionSeries')}
              label="Production Series"
              checked={values.createProductionSeries || false}
              onCheck={onCallbackChange('createProductionSeries')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              isReadOnly={isViewMode}
              value={values.internalPurchaseOrderId}
              onChange={onCallbackChange('internalPurchaseOrderId')}
              searchComponent={generateSearchEntitySelector('internalPurchaseOrder')}
              label={t('Internal Purchase Order')}
              checked={values.createInternalPO || false}
              onCheck={onCallbackChange('createInternalPO')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              isReadOnly={isViewMode}
              value={values.customerPurchaseOrderId}
              onChange={onCallbackChange('customerPurchaseOrderId')}
              searchComponent={generateSearchEntitySelector('customerPurchaseOrder')}
              label="Customer Purchase Order"
              checked={values.createCustomerPO || false}
              onCheck={onCallbackChange('createCustomerPO')}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <QuickCreateRelatedModel
              isReadOnly={isViewMode}
              value={values.qualityCPId}
              onChange={onCallbackChange('qualityCPId')}
              searchComponent={generateSearchEntitySelector('qualityCP')}
              label="Quality Control Plan"
              checked={values.createQualityCP || false}
              onCheck={onCallbackChange('createQualityCP')}
            />
          </Grid>
        </Grid>
      </Misc.NewPaperWithPadding>
    );
  };

  const _renderOurIds = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{t('Our data and IDs')}</Misc.PaperSectionTitle>
        <br />
        <TextInput
          isReadOnly={isViewMode}
          label="Part numbers"
          value={values.partNumber}
          onChangeValue={onCallbackChange('partNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Part number revision"
          value={values.partNumberRevision}
          onChangeValue={onCallbackChange('partNumberRevision')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Hardware part number revision"
          value={values.partNumberHardwareRevison}
          onChangeValue={onCallbackChange('partNumberHardwareRevison')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Software part number revision"
          value={values.partNumberSoftwareRevison}
          onChangeValue={onCallbackChange('partNumberSoftwareRevison')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Serial number"
          value={values.serialNumber}
          onChangeValue={onCallbackChange('serialNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Additional unique ID"
          value={values.logicalId2}
          onChangeValue={onCallbackChange('logicalId2')}
        />
      </Misc.NewPaperWithPadding>
    );
  };

  const _renderCustomer = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{t('Customer')}</Misc.PaperSectionTitle>
        <br />
        <TextInput
          isReadOnly={isViewMode}
          label="Customer defined ID"
          value={values.customerSerialNumber}
          onChangeValue={onCallbackChange('customerSerialNumber')}
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Customer part number"
          value={values.customerPartNumber}
          onChangeValue={onCallbackChange('customerPartNumber')}
        />
      </Misc.NewPaperWithPadding>
    );
  };

  const _renderUnits = () => {
    return (
      <Misc.NewPaperWithPadding>
        <Misc.PaperSectionTitle>{t('Units')}</Misc.PaperSectionTitle>
        <br />
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Weight"
              placeholder=""
              value={values.weight}
              onChangeValue={onCallbackChange('weight')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Weight units"
              value={values.weightUnits}
              onChangeValue={onCallbackChange('weightUnits')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Set units"
              value={values.weightSetUnits}
              onChangeValue={onCallbackChange('weightSetUnits')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Volume"
              placeholder=""
              value={values.volume}
              onChangeValue={onCallbackChange('volume')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Volume units"
              value={values.volumeUnits}
              onChangeValue={onCallbackChange('volumeUnits')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Set units"
              value={values.volumeSetUnits}
              onChangeValue={onCallbackChange('volumeSetUnits')}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="Measure units"
              value={values.measureUnits}
              onChangeValue={onCallbackChange('measureUnits')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              type="number"
              isReadOnly={isViewMode}
              label="Minimum order quantity"
              value={values.minimumOrderQuantity}
              onChangeValue={onCallbackChange('minimumOrderQuantity')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} />
        </Grid>
        <Grid container>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              type="number"
              isReadOnly={isViewMode}
              label="Number of units when packing"
              value={values.minimumNumberOfUnitsPack}
              onChangeValue={onCallbackChange('minimumNumberOfUnitsPack')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextInput
              isReadOnly={isViewMode}
              label="For each packing unit"
              value={values.packingUnit}
              onChangeValue={onCallbackChange('packingUnit')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} />
        </Grid>
      </Misc.NewPaperWithPadding>
    );
  };

  //#endregion

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={{ xs: 12, md: 6 }}>{_renderManufacturer()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>{_renderSupplier()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>{_renderProduction()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>{_renderOurIds()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>{_renderCustomer()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>{_renderUnits()}</Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextInput
          isReadOnly={isViewMode}
          label="Transport details"
          value={values.transportDetails}
          onChangeValue={onCallbackChange('transportDetails')}
        />
      </Grid>
    </Grid>
  );
};

export default WipPartInformation;
